import React from "react"
import TasksViewHeader from "../../tasks/components/TasksViewHeader"
import TaskList from "../../treegrid/task-list"
import CircularProgress from "@material-ui/core/CircularProgress"
import { Theme, makeStyles, useMediaQuery } from "@material-ui/core"
import { useRouter } from "../../hooks/use-router"
import { useUserTimeZone } from "../../hooks/use-user-time-zone"
import { TasksViewOptions } from "../../tasks/components/TasksViewSelect"
import { useDateFormat } from "../../users/hooks/use-date-format"
import { useAppContext } from "../../app-context/use-app-context"
import ErrorList from "../../components/ErrorList"
import TaskCards from "../../tasks/components/TaskCards"
import { useOptions } from "../../options/hooks/use-options"
import { useTaskListRouteData } from "./hooks/use-task-list-route-data"
import {
  taskListDefaultColumnOrderByView,
  taskListDefaultVisibleColumnsByView,
} from "./utils/task-list-column-settings"
import { useTranslation } from "react-i18next"

type Translations = typeof defaultTranslations

const TasksView = () => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"))
  const translations = useTranslations()
  const {
    appContext: { mainContext, subContext },
  } = useAppContext()
  const context = subContext || mainContext
  const { dateFormat, dateSeparator } = useDateFormat()
  const classes = useStyles()
  const userTimeZone = useUserTimeZone()
  const router = useRouter()

  // @ts-expect-error
  const tasksView = (router.query.view || "currentAndFuture") as TasksViewOptions
  const showArchivedTitle = tasksView === "archived"
  const showTaskList = !isSmallScreen
  const {
    calendarSettingsById,
    calendarSettingsQueryErrors,
    firstDayOfWeek,
    holidays,
    holidaysQueryErrors,
    isError,
    isErrorHolidays,
    isErrorProjectCalendarSettings,
    isLoading,
    isLoadingHolidays,
    isLoadingProjectCalendarSettings,
    query,
    settingsKey,
    status,
    tasks,
    weekendDays,
  } = useTaskListRouteData({ context, mainContext, showTaskList, view: tasksView })

  const [key, setKey] = React.useState(JSON.stringify(query))
  const { options } = useOptions()

  React.useEffect(() => {
    if (status === "fulfilled") {
      // Need to recreate the treegrid when the query is changed and the data is completed refetching (status = fulfilled)
      setKey(`${JSON.stringify(query)}__${settingsKey}`)
    }
  }, [status, query, settingsKey])

  if (isLoading || isLoadingProjectCalendarSettings || isLoadingHolidays) return <CircularProgress />
  if (!context || !mainContext) return <ErrorList errors={[translations.contextNotFoundError]} />
  if (isError) return <ErrorList errors={[translations.fetchErrorTasks]} />
  if (isErrorProjectCalendarSettings) return <ErrorList errors={calendarSettingsQueryErrors} />
  if (isErrorHolidays) return <ErrorList errors={holidaysQueryErrors} />

  return (
    <div className={classes.viewContainer}>
      <TasksViewHeader titlePrefix={context.name} showArchivedTitle={showArchivedTitle} />
      {isSmallScreen ? (
        <TaskCards tasks={tasks} />
      ) : (
        <TaskList
          key={key}
          tasks={tasks}
          dateFormat={dateFormat}
          timeZone={userTimeZone}
          options={options}
          gridInfo={`${context.name} - Tasks`}
          dateSeparator={dateSeparator}
          firstDayOfWeek={firstDayOfWeek}
          defaultColumnOrder={taskListDefaultColumnOrderByView[tasksView]}
          defaultVisibleColumns={taskListDefaultVisibleColumnsByView[tasksView]}
          weekendDays={weekendDays}
          holidays={holidays}
          calendarSettingsById={calendarSettingsById}
        />
      )}
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  viewContainer: {
    marginTop: theme.spacing(0.75),
    "& > :first-child": {
      marginBottom: theme.spacing(1.125),
    },
  },
}))

const defaultTranslations = {
  contextNotFoundError: "Context not found",
  fetchErrorTasks: "Failed to fetch tasks",
}

const useTranslations = (defaults = defaultTranslations): Translations => {
  const { t } = useTranslation()

  return {
    contextNotFoundError: t("contextNotFoundError", defaults.contextNotFoundError),
    fetchErrorTasks: t("fetchErrorTasks", defaults.fetchErrorTasks),
  }
}

export default TasksView
